/**
 * fidly Pixel - Core SDK
 * Versão: 1.0.0
 * 
 * Instalação:
 * <script src="https://pixel.fidly.com.br/v1/fidly.js"></script>
 * <script>
 *   Fidly.init({
 *     clientPublicToken: 'pk_...',
 *     modules: ['nps'],
 *     debug: false,
 *   });
 * </script>
 * 
 * Após o login do usuário:
 *   Fidly.identify('user_id', {
 *     email: 'user@empresa.com',
 *     name: 'Nome',
 *     job: 'owner',
 *     accountExternalId: 'empresa_id'
 *   });
 */

(function(window, document) {
  'use strict';

  // Evitar carregar múltiplas vezes
  if (window.Fidly) {
    console.warn('fidly: SDK already loaded');
    return;
  }

  // Configuração
  const VERSION = '1.0.0';
  const BUNDLE_URL = import.meta.env.VITE_BUNDLE_URL || 'https://pixel.fidly.com.br/v1/fidly.bundle.js';
  const DEFAULT_API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || 'https://api-pixel.fidly.com.br';

  // Base do CDN: extrai a origem da URL do bundle
  const CDN_BASE = new URL(BUNDLE_URL).origin;
  
  // SRI (Subresource Integrity) - apenas em produção
  // Gerado automaticamente no build: npm run generate-integrity
  const BUNDLE_INTEGRITY = null; // Será preenchido no build de produção
  
  // Para desenvolvimento local: const BUNDLE_URL = 'http://localhost:3000/fidly.bundle.js';

  /**
   * SDK Principal
   */
  const Fidly = {
    version: VERSION,
    config: null,
    isLoaded: false,
    isInitialized: false,
    queue: [],
    modules: {},
    
    /**
     * Inicializar SDK
     * @param {Object} config - Configurações
     */
    init: function(config) {
      // Guard: evitar double init — mas processar módulos novos se necessário
      if (this.isInitialized) {
        const newUserId = config && config.userId;
        const userChanged = newUserId && newUserId !== this.config.userId;

        if (userChanged && this.config.autoIdentify) {
          // Novo usuário detectado na mesma sessão (ex: logout + login SPA)
          // Atualizar config e re-identificar — o cache em identify() já garante
          // que um page refresh com o mesmo usuário não dispara chamada à API
          this.config.userId            = newUserId;
          this.config.userEmail         = config.userEmail         || null;
          this.config.userName          = config.userName          || null;
          this.config.userJob           = config.userJob           || null;
          this.config.accountExternalId = config.accountExternalId || null;
          this.config.accountName       = config.accountName       || null;
          this.config.accountDocumentId = config.accountDocumentId || null;
          this.config.accountStatus     = config.accountStatus     || null;
          this.config.accountStartedAt  = config.accountStartedAt  || null;

          const traits = {
            email:             this.config.userEmail,
            name:              this.config.userName,
            job:               this.config.userJob,
            accountExternalId: this.config.accountExternalId,
            accountName:       this.config.accountName,
            accountDocumentId: this.config.accountDocumentId,
            accountStatus:     this.config.accountStatus,
            accountStartedAt:  this.config.accountStartedAt,
          };
          Object.keys(traits).forEach(key => {
            if (traits[key] == null) delete traits[key];
          });

          if (this.config.debug) {
            console.log('fidly: New user detected, re-identifying', newUserId);
          }

          // Limpa o registry de módulos para que sejam re-inicializados com o novo usuário.
          // O guard interno de cada módulo (ex: nps.js) evita double-init com o mesmo usuário.
          this.modules = {};

          this.identify(newUserId, traits);
        } else if (this.config.debug) {
          console.warn('fidly: init() already called. Use identify() to update user data.');
        }
        // Carregar módulos que ainda não foram carregados
        if (config && config.modules && this.isLoaded) {
          config.modules.forEach(function(moduleName) {
            if (!Fidly.modules[moduleName]) {
              Fidly.loadModule(moduleName);
            }
          });
        }
        return this;
      }

      // Validações
      if (!config) {
        console.error('fidly: config is required');
        return this;
      }

      if (!config.clientPublicToken) {
        console.error('fidly: clientPublicToken is required');
        return this;
      }

      // Configuração padrão
      this.config = {
        clientPublicToken: config.clientPublicToken,
        accountExternalId:  config.accountExternalId  || null,
        accountName:        config.accountName        || null,
        accountDocumentId:  config.accountDocumentId  || null,
        accountStatus:      config.accountStatus      || null,
        accountStartedAt:   config.accountStartedAt   || null,

        // Usuário (opcional)
        userId:    config.userId    || null,
        userEmail: config.userEmail || null,
        userName:  config.userName  || null,
        userJob:   config.userJob   || null,
        
        // Endpoints
        apiEndpoint: config.apiEndpoint || DEFAULT_API_ENDPOINT,
        cdnEndpoint: config.cdnEndpoint || CDN_BASE,
        
        // Comportamento
        //autoTrack: config.autoTrack !== false,
        autoTrack: config.autoTrack === true,
        autoIdentify: config.autoIdentify !== true,
        autoGenerateAnonymousId: config.autoGenerateAnonymousId || false,
        
        // Módulos
        modules: config.modules || ['nps','help_center'],
        
        // UI
        theme: config.theme || 'light',
        language: config.language || this.detectLanguage(),
        position: config.position || 'center',
        
        // Metadados
        metadata: config.metadata || {},
        
        // Callbacks
        onReady: config.onReady || null,
        onError: config.onError || null,
        onEvent: config.onEvent || null,
        
        // Debug
        debug: config.debug || false
      };

      this.isInitialized = true;

      if (this.config.debug) {
        console.log('fidly: Initialized with config', {
          clientPublicToken: this.config.clientPublicToken.substring(0, 8) + '...',
          accountExternalId: this.config.accountExternalId,
          userId: this.config.userId,
          apiEndpoint: this.config.apiEndpoint
        });
      }

      // Gerar/recuperar anonymousId se necessário
      if (!this.config.userId && this.config.autoGenerateAnonymousId) {
        this.config.userId = this.generateAnonymousId();
        
        if (this.config.debug) {
          console.log('fidly: Using anonymous ID:', this.config.userId);
        }
      }

      // Auto-identify usuário se userId foi fornecido e autoIdentify está ativo
      if (this.config.autoIdentify && this.config.userId) {
        const traits = {
          email:             this.config.userEmail,
          name:              this.config.userName,
          job:               this.config.userJob,
          accountExternalId: this.config.accountExternalId,
          accountName:       this.config.accountName,
          accountDocumentId: this.config.accountDocumentId,
          accountStatus:     this.config.accountStatus,
          accountStartedAt:  this.config.accountStartedAt,
        };
        
        // Remover propriedades undefined
        Object.keys(traits).forEach(key => {
          if (traits[key] === null || traits[key] === undefined) {
            delete traits[key];
          }
        });

        if (this.config.debug) {
          console.log('fidly: Auto-identifying user', this.config.userId);
        }

        this.identify(this.config.userId, traits);
      }

      // Auto-track pageview
      if (this.config.autoTrack) {
        this.page();
      }

      // Carregar bundle
      this.loadBundle();

      return this;
    },

    /**
     * Carregar bundle principal
     */
    loadBundle: function() {
      if (this.isLoaded) {
        if (this.config.debug) {
          console.log('fidly: Bundle already loaded');
        }
        return;
      }

      const script = document.createElement('script');
      script.src = BUNDLE_URL;
      script.async = true;
      script.crossOrigin = 'anonymous';
      
      if (BUNDLE_INTEGRITY) {
        script.integrity = BUNDLE_INTEGRITY;
      }
      
      script.onload = () => {
        this.isLoaded = true;
        
        if (this.config.debug) {
          console.log('fidly: Bundle loaded successfully');
        }

        // Carregar storage imediatamente após o bundle
        // Garante que FidlyStorage está disponível antes do identify()
        window.FidlyBundle.loadStorage(this.config).then(() => {
          // Processar fila de comandos (pode conter identify pendente)
          this.processQueue();

          // Callback onReady
          if (this.config.onReady && typeof this.config.onReady === 'function') {
            try {
              this.config.onReady();
            } catch (error) {
              console.error('fidly: Error in onReady callback', error);
            }
          }
        });
      };

      script.onerror = () => {
        console.error('fidly: Failed to load bundle from', BUNDLE_URL);
        
        if (this.config.onError && typeof this.config.onError === 'function') {
          try {
            this.config.onError(new Error('Failed to load bundle'));
          } catch (error) {
            console.error('fidly: Error in onError callback', error);
          }
        }
      };

      document.head.appendChild(script);
    },

    /**
     * Processar fila de comandos
     */
    processQueue: function() {
      if (!this.queue) this.queue = [];
      while (this.queue.length > 0) {
        const [method, args] = this.queue.shift();
        if (typeof this[method] === 'function') {
          this[method].apply(this, args);
        }
      }
    },

    /**
     * Adicionar comando à fila
     */
    enqueue: function(method, args) {
      if (!this.queue) this.queue = [];
      this.queue.push([method, args]);
    },

    /**
     * Identificar usuário
     * @param {string} userId - ID do usuário
     * @param {Object} traits - Atributos do usuário
     */
    identify: function(userId, traits) {
      try {
      if (!this.isInitialized) {
        console.error('fidly: SDK not initialized. Call init() first');
        return this;
      }

      if (!this.isLoaded) {
        this.enqueue('identify', [userId, traits]);
        return this;
      }

      traits = traits || {};

      // accountExternalId é obrigatório no identify
      if (!traits.accountExternalId) {
        console.error('fidly: accountExternalId is required in identify()');
        return this;
      }

      // Atualizar config — sempre, independente do cache
      this.config.userId            = userId;
      this.config.accountExternalId = traits.accountExternalId;
      this.config.accountName       = traits.accountName       || this.config.accountName;
      this.config.accountDocumentId = traits.accountDocumentId || this.config.accountDocumentId;
      this.config.accountStatus     = traits.accountStatus     || this.config.accountStatus;
      this.config.accountStartedAt  = traits.accountStartedAt  || this.config.accountStartedAt;
      this.config.userEmail         = traits.email             || this.config.userEmail;
      this.config.userName          = traits.name              || this.config.userName;
      this.config.userJob           = traits.job               || this.config.userJob;
      
      // Merge metadata
      if (traits.metadata) {
        this.config.metadata = Object.assign({}, this.config.metadata, traits.metadata);
      }

      // Verificar cache — se já identificado nas últimas 12h, só carregar módulos sem request
      const alreadyIdentified = window.FidlyStorage
        ? !!window.FidlyStorage.getCached('user', userId)
        : false;

      if (alreadyIdentified) {
        if (this.config.debug) {
          console.log('fidly: identify skipped (cache valid)', { userId });
        }
        // Módulos ainda precisam ser carregados mesmo com cache
        if (this.isLoaded && this.config.modules && this.config.modules.length > 0) {
          this.config.modules.forEach(module => {
            if (!this.modules[module]) this.loadModule(module);
          });
        }
        return this;
      }

      if (this.config.debug) {
        console.log('fidly: User identified', { userId, traits });
      }

      // Enviar para bundle — bundle gerencia o cache após envio bem-sucedido
      if (window.FidlyBundle && window.FidlyBundle.identify) {
        window.FidlyBundle.identify(userId, traits, this.config);
      }

      // Carregar módulos após identificar — user já está no storage
      if (this.isLoaded && this.config.modules && this.config.modules.length > 0) {
        this.config.modules.forEach(module => {
          if (!this.modules[module]) this.loadModule(module);
        });
      }

      return this;
      } catch (error) {
        console.error('fidly: Error in identify()', error);
        return this;
      }
    },

    /**
     * Enviar evento tipado do sistema
     * @param {string} eventType - Tipo do evento (ex: 'login_success', 'critical_feature_used')
     * @param {Object} properties - Dados de negócio sobre o evento (ex: { feature_name: 'automation_builder' })
     * @param {Object} metadata - Contexto técnico opcional (ex: { source: 'mobile_app' })
     */
    event: function(eventType, properties, metadata) {
      try {
        if (!this.isInitialized) {
          console.error('fidly: SDK not initialized. Call init() first');
          return this;
        }

        if (!this.isLoaded) {
          this.enqueue('event', [eventType, properties, metadata]);
          return this;
        }

        if (!this.config.accountExternalId) {
          console.error('fidly: accountExternalId is required to send events');
          return this;
        }

        if (window.FidlyBundle && window.FidlyBundle.sendEvent) {
          window.FidlyBundle.sendEvent(eventType, properties, metadata, this.config);
        }

        return this;
      } catch (error) {
        console.error('fidly: Error in event()', error);
        return this;
      }
    },

    /**
     * Rastrear evento
     * @param {string} eventName - Nome do evento
     * @param {Object} properties - Propriedades do evento
     */
    track: function(eventName, properties) {
      try {
      if (!this.isInitialized) {
        console.error('fidly: SDK not initialized. Call init() first');
        return this;
      }

      if (!this.isLoaded) {
        this.enqueue('track', [eventName, properties]);
        return this;
      }

      properties = properties || {};

      if (this.config.debug) {
        console.log('fidly: Event tracked', eventName, properties);
      }

      // Callback onEvent
      if (this.config.onEvent && typeof this.config.onEvent === 'function') {
        try {
          this.config.onEvent(eventName, properties);
        } catch (error) {
          console.error('fidly: Error in onEvent callback', error);
        }
      }

      // Enviar para bundle
      if (window.FidlyBundle && window.FidlyBundle.track) {
        window.FidlyBundle.track(eventName, properties, this.config);
      }

      return this;
      } catch (error) {
        console.error('fidly: Error in track()', error);
        return this;
      }
    },

    /**
     * Rastrear pageview
     * @param {string} name - Nome da página
     * @param {Object} properties - Propriedades da página
     */
    page: function(name, properties) {
      if (!this.isInitialized) {
        console.error('fidly: SDK not initialized. Call init() first');
        return this;
      }

      properties = properties || {};
      
      // Auto-detectar informações da página
      const pageData = {
        name: name || document.title,
        path: properties.path || window.location.pathname,
        url: properties.url || window.location.href,
        title: properties.title || document.title,
        referrer: properties.referrer || document.referrer,
        search: properties.search || window.location.search,
        ...properties
      };

      return this.track('page_viewed', pageData);
    },

    /**
     * Carregar módulo
     * @param {string} moduleName - Nome do módulo (nps, widgets, analytics)
     */
    loadModule: function(moduleName) {
      if (!this.isInitialized) {
        console.error('fidly: SDK not initialized. Call init() first');
        return this;
      }

      if (!this.isLoaded) {
        this.enqueue('loadModule', [moduleName]);
        return this;
      }

      if (this.modules[moduleName]) {
        if (this.config && this.config.debug) {
          console.log('fidly: Module already loaded', moduleName);
        }
        return this;
      }

      if (this.config.debug) {
        console.log('fidly: Loading module', moduleName);
      }

      // Enviar para bundle
      if (window.FidlyBundle && window.FidlyBundle.loadModule) {
        window.FidlyBundle.loadModule(moduleName, this.config)
          .then(module => {
            this.modules[moduleName] = module;
            this[moduleName] = module;
            
            if (this.config.debug) {
              console.log('fidly: Module loaded', moduleName);
            }
          })
          .catch(error => {
            console.error('fidly: Failed to load module', moduleName, error);
          });
      }

      return this;
    },

    /**
     * Atualizar configuração
     * @param {Object} updates - Atualizações de configuração
     */
    updateConfig: function(updates) {
      if (!this.isInitialized) {
        console.error('fidly: SDK not initialized. Call init() first');
        return this;
      }

      this.config = Object.assign({}, this.config, updates);

      if (this.config.debug) {
        console.log('fidly: Config updated', updates);
      }

      // Notificar bundle
      if (window.FidlyBundle && window.FidlyBundle.updateConfig) {
        window.FidlyBundle.updateConfig(this.config);
      }

      return this;
    },

    /**
     * Ativar/desativar debug
     * @param {boolean} enabled - Ativar debug
     */
    debug: function(enabled) {
      if (this.config) {
        this.config.debug = enabled !== false;
        console.log('fidly: Debug mode', this.config.debug ? 'enabled' : 'disabled');
      }

      return this;
    },

    /**
     * Detectar idioma do navegador
     */
    detectLanguage: function() {
      const lang = navigator.language || navigator.userLanguage || 'en-US';
      
      // Mapear para idiomas suportados
      if (lang.startsWith('pt')) return 'pt-BR';
      if (lang.startsWith('es')) return 'es-ES';
      return 'en-US';
    },

    /**
     * Gerar ou recuperar anonymousId
     */
    generateAnonymousId: function() {
      try {
        const ANON_KEY = 'fidly_anon_id';

        // Tentar recuperar existente — via FidlyStorage ou localStorage direto
        let anonymousId = window.FidlyStorage
          ? window.FidlyStorage.getAnonymousId()
          : localStorage.getItem(ANON_KEY);

        if (anonymousId) {
          if (this.config && this.config.debug) {
            console.log('fidly: Reusing existing anonymous ID');
          }
          return anonymousId;
        }

        // Gerar novo
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 11);
        anonymousId = `anon_${timestamp}_${random}`;

        // Salvar — via FidlyStorage ou localStorage direto
        if (window.FidlyStorage) {
          window.FidlyStorage.setAnonymousId(anonymousId);
        } else {
          localStorage.setItem(ANON_KEY, anonymousId);
        }

        if (this.config && this.config.debug) {
          console.log('fidly: Generated new anonymous ID');
        }

        return anonymousId;
      } catch (error) {
        console.error('fidly: Error generating anonymous ID', error);
        
        // Fallback: gerar ID sem persistência
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 11);
        return `anon_${timestamp}_${random}`;
      }
    },

    /**
     * Obter informações do SDK
     */
    getInfo: function() {
      const isAnonymous = this.config && this.config.userId && this.config.userId.startsWith('anon_');
      
      return {
        version: this.version,
        isInitialized: this.isInitialized,
        isLoaded: this.isLoaded,
        queueLength: this.queue.length,
        loadedModules: Object.keys(this.modules),
        config: this.config ? {
          clientPublicToken: this.config.clientPublicToken.substring(0, 10) + '...',
          accountExternalId: this.config.accountExternalId,
          userId: this.config.userId,
          userEmail: this.config.userEmail,
          isAnonymous: isAnonymous,
          autoGenerateAnonymousId: this.config.autoGenerateAnonymousId
        } : null
      };
    },

    /**
     * Reset SDK (útil para testes)
     */
    reset: function() {
      // Limpar cache do identify do usuário atual
      if (window.FidlyStorage && this.config && this.config.userId) {
        window.FidlyStorage.clearCached('user', this.config.userId);
      }

      this.config = null;
      this.isLoaded = false;
      this.isInitialized = false;
      this.queue = [];
      this.modules = {};

      if (window.FidlyBundle && window.FidlyBundle.reset) {
        window.FidlyBundle.reset();
      }

      // Limpar anonymousId do localStorage
      if (window.FidlyStorage) {
        window.FidlyStorage.clearAnonymousId();
      }

      console.log('fidly: SDK reset');
      return this;
    }
  };

  // Expor globalmente
  window.Fidly = Fidly;

  // Log de carregamento
  if (console && console.log) {
    console.log('fidly SDK v' + VERSION + ' loaded');
  }

})(window, document);
